import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { arrayBufferToBase64 } from '../lib/crypto';
import { setConversationKeyRaw } from '../lib/conversation-crypto';
import { t } from '../lib/i18n';
import { api } from '../lib/trpc/client';

type GroupSummary = { groupId: string; name: string; role: string; updatedAt: string };

function displayRole(role: string): string {
  if (role === 'owner') return t('オーナー', 'Owner');
  if (role === 'admin') return t('管理者', 'Admin');
  if (role === 'moderator') return t('モデレーター', 'Moderator');
  if (role === 'member') return t('メンバー', 'Member');
  return role;
}

export function GroupsPage() {
  const { circleId } = useParams<{ circleId: string }>();
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [activeCircleName, setActiveCircleName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [result, activeCircle] = await Promise.all([
        api.group.getGroups(),
        api.circle.getActiveCircle()
      ]);
      setGroups(result.groups);
      setActiveCircleName(activeCircle?.name ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('グループ一覧の取得に失敗しました', 'Failed to load groups'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const createGroup = async () => {
    const normalized = name.trim();
    if (!normalized) {
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
      const raw = (await crypto.subtle.exportKey('raw', key)) as ArrayBuffer;
      const rawBase64 = arrayBufferToBase64(raw);

      const created = await api.group.createGroup({
        name: normalized,
        members: [],
        selfWrappedKey: { wrappedKey: rawBase64, ephemeralPk: rawBase64 }
      });
      setConversationKeyRaw('group', created.groupId, rawBase64);
      setName('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('グループ作成に失敗しました', 'Failed to create group'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <main className="mx-auto min-h-screen w-full max-w-2xl space-y-4 px-4 py-10">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold">{t('グループ', 'Groups')}</h1>
        <p className="text-sm text-muted-foreground">{t('グループ会話の一覧と作成を行います。', 'Create groups and browse your group conversations.')}</p>
        <p className="text-xs text-muted-foreground">{t('サークル', 'Circle')}: {activeCircleName || t('未選択', 'Not selected')}</p>
      </header>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('新しいグループ', 'New group')}</CardTitle>
          <CardDescription>{t('まず自分だけのグループを作成し、後からメンバーを追加します。', 'Create a group first, then add members later.')}</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('グループ名', 'Group name')} />
          <Button onClick={createGroup} disabled={creating || !name.trim()}>
            {creating ? t('作成中...', 'Creating...') : t('作成', 'Create')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('グループ一覧', 'Groups')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : groups.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('グループはまだありません。', 'No groups yet.')}</p>
          ) : (
            <div className="space-y-2">
              {groups.map((group) => (
                <Link
                  key={group.groupId}
                  to={`/${circleId}/groups/${group.groupId}`}
                  className="flex items-center justify-between rounded-md border border-border p-3 text-sm hover:bg-muted"
                >
                  <span>{group.name}</span>
                  <span className="text-muted-foreground">{displayRole(group.role)}</span>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
